// Section: Effective Tax Settings (gated by partsFinancialEngine)
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';

interface Props {
  combinedRate: number;
  stateRate: number;
  localRate: number;
  sourceLabel?: string | null;
  manualOverride: boolean;
  onChange: (next: { combinedRate: number; stateRate: number; localRate: number }) => void;
}

export function EffectiveTaxSection({
  combinedRate, stateRate, localRate, sourceLabel, manualOverride, onChange,
}: Props) {
  const enabled = useFeatureFlag('partsFinancialEngine');
  if (!enabled) return null;

  const handle = (key: 'combinedRate' | 'stateRate' | 'localRate') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const pct = parseFloat(e.target.value);
    let decimal = isNaN(pct) ? 0 : Number((pct / 100).toFixed(6));
    // Phase 1 hardening: clamp to [0, 0.30]. Negative or > 30% rejected.
    if (decimal < 0) decimal = 0;
    if (decimal > 0.30) decimal = 0.30;
    onChange({
      combinedRate, stateRate, localRate,
      [key]: decimal,
    });
  };

  const toPct = (v: number) => Number((v * 100).toFixed(4));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Effective Tax Settings
          {manualOverride
            ? <Badge variant="outline" className="text-xs">Manual override</Badge>
            : sourceLabel && <Badge variant="secondary" className="text-xs">{sourceLabel}</Badge>}
        </CardTitle>
        <CardDescription>Rates applied during calculation. Expressed as percentages (e.g. 8.75 = 8.75%). Maximum 30%.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Combined Rate (%)</Label>
            <div className="relative">
              <Input type="number" step="0.01" min="0" max="30" value={toPct(combinedRate)} onChange={handle('combinedRate')} className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label>State Rate (%)</Label>
            <div className="relative">
              <Input type="number" step="0.01" min="0" max="30" value={toPct(stateRate)} onChange={handle('stateRate')} className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Local Rate (%)</Label>
            <div className="relative">
              <Input type="number" step="0.01" min="0" max="30" value={toPct(localRate)} onChange={handle('localRate')} className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
